import { connect, type ConnectedProps } from "react-redux";

import { DetailsHeader } from "components";
import AddAssetsToPackModal from "components/modal/AddAssetsToPacksModal/AddAssetsToPackModal";
import { type OutstandingDetailsHeaderOwnProps } from "components/sectionHeader/detailsHeader/types";
import SelectedItemsLabel from "components/sectionHeader/selectedItemsLabel/SelectedItemsLabel";
import { RolePermissions } from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import Restricted from "features/Application/Restricted";
import { AddPacksButton } from "features/Licensing/Packs/AssociatedPacks/AddPacksButton";
import { bindAction } from "interfaces";
import { noop } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { getPeopleEntityLabel } from "utils/peopleAssignmentModalUtils";
import { lockEmbeddedVideo } from "../state/thunks/EmbeddedVideosEntityStateThunk";

interface EditEmbeddedVideoDetailsHeaderContainerPropsBase extends OutstandingDetailsHeaderOwnProps {
  embeddedVideoId: number;
  overviewUrl: string;
  canAddPacks: boolean;
  showAddPacksButton: boolean;
  back: () => void;
}

export type EditEmbeddedVideoDetailsHeaderProps = EditEmbeddedVideoDetailsHeaderContainerPropsBase & PropsFromRedux;

export const EditEmbeddedVideoDetailsHeader = (props: EditEmbeddedVideoDetailsHeaderProps) => {
  const {
    lockEmbeddedVideo,
    selectedUserIds,
    selectedGroupIds,
    back,
    canAddPacks,
    embeddedVideoId,
    title,
    overviewUrl,
    showAddPacksButton,
  } = props;

  const [packsModalShown, setPacksModalShown] = useState(false);
  const renderAddPackButton = (hasAnyPermission: boolean) => (
    <AddPacksButton disabled={!canAddPacks} hasPermission={hasAnyPermission} onClick={() => setPacksModalShown(true)} />
  );

  const navigate = useNavigate();
  const onClose = useCallback(() => navigate(overviewUrl), [navigate, overviewUrl]);
  const onEdit = useCallback(() => lockEmbeddedVideo(embeddedVideoId), [lockEmbeddedVideo, embeddedVideoId]);

  const headerTitle = useMemo(() => {
    const selectedPeopleCount = selectedUserIds.length + selectedGroupIds.length;

    if (selectedPeopleCount) {
      return (
        <SelectedItemsLabel
          itemName={getPeopleEntityLabel(selectedUserIds.length, selectedGroupIds.length)}
          selectedItemsAmount={selectedPeopleCount}
          shouldPluralize={false}
        />
      );
    } else {
      return title;
    }
  }, [title, selectedGroupIds.length, selectedUserIds.length]);

  return (
    <DetailsHeader
      title={headerTitle}
      pageTitle={props.title}
      titleForGA="Asset Details"
      backButton={back || overviewUrl}
      defaultURL={overviewUrl}
      canBeEdited={props.canBeEdited}
      crudPermission={props.crudPermission}
      canBePublished={props.canBePublished}
      isRevertVisible={props.isRevertVisible}
      invalidFormDetails={props.invalidFormDetails}
      publishedStatus={props.publishedStatus}
      entityStateActions={{
        onClose,
        onEdit,
        onPublish: props.entityStateActions.onPublish,
        onRevert: props.entityStateActions.onRevert,
      }}
      hideEntityStateButtons={props.hideEntityStateButtons}
    >
      {showAddPacksButton && (
        <Restricted
          permissions={[RolePermissions.AssetsCreate, RolePermissions.PacksManage]}
          renderContent={(hasanyPermission) => renderAddPackButton(hasanyPermission)}
        />
      )}
      <AddAssetsToPackModal
        showModal={packsModalShown}
        onClose={() => setPacksModalShown(false)}
        selectedItemIds={[embeddedVideoId]}
        contentType="EmbeddedVideo"
        onComplete={noop}
      />
    </DetailsHeader>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const embeddedVideoEdit = state.library.embeddedVideos.edit;
  return {
    isPeopleSelected: !!(embeddedVideoEdit.users.selectedIds.length || embeddedVideoEdit.groups.selectedIds.length),
    selectedUserIds: embeddedVideoEdit.users.selectedIds,
    selectedGroupIds: embeddedVideoEdit.groups.selectedIds,
    selectedUsers: embeddedVideoEdit.users.selectedItems,
    selectedGroups: embeddedVideoEdit.groups.selectedItems,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  lockEmbeddedVideo: bindAction(lockEmbeddedVideo, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditEmbeddedVideoDetailsHeader);
export default ConnectedComponent;
